import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Avatar, Button, Col, Input, Row, Tag } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import { itemRender, onShowSizeChange } from "../../../commons/PaginationFunction";
import AddEditProject from "../../../commons/Projects/AddEditProject";
import { Error } from "../../../commons/Messages/Messages";
import axios from "axios";
import DeleteModal from "../../../commons/DeleteModal/DeleteModal";
import SelectComp from "../../../commons/Select/Select";
import DropdownComp from "../../../commons/Dropdowns/DropdownComp";
import ResponsiveTable from "../../../design-system/ResponsiveTable";
import PageToolbar from "../../../design-system/PageToolbar";
import "./SubadminProjects.css";
import defaultImage from "../../../commons/PanelAssets/img/doctors/doctor-01.jpg";

import { useTranslation } from "react-i18next";
const Projects = () => {
  const { t } = useTranslation("dashboard");
  const [data, setData] = useState([]);

  const columns = [
    {
      title: t("Projects ID"),
      dataIndex: "_id",
      mobile: "subtitle",
      sorter: (a, b) => a.projects_id.length - b.projects_id.length
    },
    {
      title: t("Name"),
      dataIndex: "projectName",
      mobile: "title",
      render: (text, record) => <strong>{record.projectName}</strong>,
      sorter: (a, b) => a.firstName.localeCompare(b.firstName)
    },
    {
      title: t("ServiceProvider"),
      dataIndex: "serviceProvider",
      render: (text, record) => (
        <Link to="/app/projects/projects-profile" className="subadmin-projects__person">
          <Avatar
            size={40}
            alt=""
            src={
              record.serviceProvider?.picture?.url
                ? record.serviceProvider?.picture?.url
                : defaultImage
            }
          />
          <span>
            {record?.serviceProvider?.firstName} {record?.serviceProvider?.lastName}
          </span>
        </Link>
      ),
      sorter: (a, b) => a.firstName.localeCompare(b.firstName)
    },
    {
      title: t("Start Date"),
      dataIndex: "startDate",
      sorter: (a, b) => a.startDate.length - b.startDate.length
    },
    {
      title: t("End Date"),
      dataIndex: "endDate",
      sorter: (a, b) => a.endDate.length - b.endDate.length
    },
    {
      title: t("Priority"),
      dataIndex: "priority",
      sorter: (a, b) => a.priority.localeCompare(b?.localeCompare),
      render: (text, record) =>
        record.priority === "MEDIUM" ? (
          <Tag color="orange">{t("MEDIUM")}</Tag>
        ) : record.priority === "HIGH" ? (
          <Tag color="red">{t("HIGH")}</Tag>
        ) : record.priority ? (
          <Tag color="green">{t(record.priority)}</Tag>
        ) : null
    },
    {
      title: t("Status"),
      dataIndex: "status",
      sorter: (a, b) => a.status.length - b.status.length
    },
    {
      title: t("Status"),
      key: "statusSelect",
      dataIndex: "status",
      mobile: "hidden",
      render: text => (
        <div className="subadmin-projects__status">
          <SelectComp
            handleChange={() => undefined}
            value="Active"
            options={[
              {
                value: "JUST ADDED",
                label: t("JUST ADDED")
              },
              {
                value: "IN PROGRESS",
                label: t("IN PROGRESS")
              },
              {
                value: "ISSUES",
                label: t("ISSUES")
              },
              {
                value: "REJECTED",
                label: t("REJECTED")
              },
              {
                value: "COMPLETED",
                label: t("COMPLETED")
              },
              {
                value: "REVISION",
                label: t("REVISION")
              }
            ]}
          />
        </div>
      )
    },
    {
      title: t("Action"),
      key: "action",
      render: (text, obj) => (
        <DropdownComp
          menuItems={[
            {
              key: "edit",
              component: () => <AddEditProject data={obj} updateParent={getData} />
            },
            {
              key: "delete",
              component: () => <DeleteModal text="Project" onClick={() => undefined} />
            }
          ]}
        />
      )
    }
  ];

  const getData = async () => {
    await axios
      .get(`${process.env.REACT_APP_BACKEND_URL}/projects`, {
        headers: {
          Authorization: "Bearer " + localStorage.getItem("token")
        }
      })
      .then(res => {
        if (res.status === 200 || res.status === 304) {
          setData(res.data);
        } else {
          Error(res.data.errorMessage);
        }
      })
      .catch(err => {
        if (err.response?.data?.errorMessage) {
          Error(err.response?.data?.errorMessage);
        } else {
          Error(err.message);
        }
      });
  };

  useEffect(() => {
    getData();

    return () => {};
  }, []);

  // The shell (PanelsHeader) renders the page title, breadcrumb and navigation.
  return (
    <div className="subadmin-projects">
      <PageToolbar
        filters={
          <Row gutter={[8, 8]} className="subadmin-projects__filters">
            <Col xs={24} sm={12} lg={6}>
              <Input
                placeholder={t("Projects ID")}
                aria-label={t("Projects ID")}
                autoComplete="off"
              />
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Input
                placeholder={t("Projects Name")}
                aria-label={t("Projects Name")}
                autoComplete="off"
              />
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <SelectComp
                handleChange={() => undefined}
                value="1"
                options={[
                  {
                    value: "1",
                    label: t("New Company")
                  },
                  {
                    value: "2",
                    label: t("New Company 2")
                  }
                ]}
              />
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Button type="primary" block icon={<SearchOutlined />}>
                {t("Search")}
              </Button>
            </Col>
          </Row>
        }
        actions={<AddEditProject updateParent={getData} />}
      />
      <ResponsiveTable
        pagination={{
          total: data.length,
          showTotal: (total, range) =>
            t("pagination.showTotal", { rangeStart: range[0], rangeEnd: range[1], total }),
          showSizeChanger: true,
          onShowSizeChange: onShowSizeChange,
          itemRender: itemRender
        }}
        columns={columns}
        dataSource={data}
        rowKey={record => record._id ?? record.id}
      />
    </div>
  );
};

export default Projects;
